import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  notification,
  Row,
  Select,
  Timeline,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  STORAGE_DELIVERY_CITY,
  STORAGE_DELIVERY_COUNTRY,
  STORAGE_DELIVERY_HOUSENUMBER,
  STORAGE_DELIVERY_HOUSENUMBER_ADDITION,
  STORAGE_DELIVERY_STREET,
  STORAGE_DELIVERY_ZIP,
} from "../../constants/LocalStorageConstants";
import styled from "styled-components";
import { lookupAddress } from "../../services/api/postcode";
import { COUNTRY_BE, COUNTRY_NL } from "../../constants/CountryConstants";
import {
  validateAddition,
  validateHouseNumber,
  validateZipCode,
} from "../../helpers/validators";
import { SmileOutlined } from "@ant-design/icons";
import { isLandelijk } from "../../helpers/region";

const nextStepsLandelijk = {
  nl: {
    title: "🥩 Zo wordt jouw bestelling geleverd:",
    steps: [
      {
        title: "Vacuum verpakt per artikel",
        description:
          "Elk stuk vlees wordt zorgvuldig per artikel vacuüm verpakt aan de hand van het door jou bestelde gewicht voor maximale versheid. Zo ontvang jij ieder stukje vlees op de veiligste en optimale manier.",
      },
      {
        title: "Vriesvers ingevroren",
        description:
          "Na het verpakken wordt het vlees direct diepgevroren, zodat smaak en kwaliteit behouden blijven.",
      },
      {
        title: "Verzending in gekoelde doos",
        description:
          "De bestelling wordt geleverd in een stevige, geïsoleerde koeldoos die het vlees 100% op de juiste temperatuur houdt tot aan moment van ontvangst.",
      },
      {
        title: "Bezorging aan huis",
        description:
          "Wij bezorgen dagelijks tussen 17:00 en 22:00 door heel Nederland & België, direct bij jou thuis met live track & trace.",
        dot: <SmileOutlined />,
      },
    ],
  },
  en: {
    title: "🥩 How your order is delivered:",
    steps: [
      {
        title: "Packed vacuum packed per item",
        description:
          "Each piece of meat is carefully vacuum packed according to the weight you ordered for maximum freshness. So you get each piece of meat in the safest and most optimal way.",
      },
      {
        title: "Frozen solid",
        description:
          "After packing, the meat is frozen immediately to preserve taste and quality.",
      },
      {
        title: "Delivery in insulated box",
        description:
          "The order is delivered in a sturdy, insulated box that keeps the meat at the correct temperature until delivery.",
      },
      {
        title: "Home delivery",
        description:
          "We deliver daily from 17:00 to 22:00 throughout the Netherlands and Belgium, right to your door with live tracking.",
        dot: <SmileOutlined />,
      },
    ],
  },
};

const nextStepsRegional = {
  nl: {
    title: "🥩 Zo wordt jouw bestelling geleverd:",
    steps: [
      {
        title: "Vacuum verpakt per artikel",
        description:
          "Elk stuk vlees wordt zorgvuldig per artikel vacuüm verpakt aan de hand van het door jou bestelde gewicht voor maximale versheid. Zo ontvang jij ieder stukje vlees op de veiligste en optimale manier.",
      },
      {
        title: "Vriesvers ingevroren",
        description:
          "Het bestelde artikel wordt direct vers ingevroren, zodat smaak en kwaliteit behouden blijven.",
      },
      {
        title: "Verzending in gekoelde doos",
        description:
          "De bestelling wordt geleverd met onze gekoelde bezorgbus, die het vlees 100% op de juiste temperatuur houdt tot aan moment van ontvangst.",
      },
      {
        title: "Bezorging aan huis",
        description:
          "Wij bezorgen op woensdag, donderdag en vrijdag tussen 14:00 en 18:00 door heel West-Friesland, direct bij jou thuis met live track & trace.",
        dot: <SmileOutlined />,
      },
    ],
  },
  en: {
    title: "🥩 How your order is delivered:",
    steps: [
      {
        title: "Vacuum packed per item",
        description:
          "Each piece of meat is carefully vacuum packed according to the weight you ordered for maximum freshness. So you get each piece of meat in the safest and most optimal way.",
      },
      {
        title: "Frozen packed",
        description:
          "The ordered item is immediately frozen fresh, so that taste and quality are preserved.",
      },
      {
        title: "Delivery in insulated box",
        description:
          "The order is delivered with our insulated delivery box, which keeps the meat at the correct temperature until delivery.",
      },
      {
        title: "Home delivery",
        description:
          "We deliver on Wednesday, Thursday and Friday between 14:00 and 18:00 in the West-Friesland region, right to your door with live tracking.",
        dot: <SmileOutlined />,
      },
    ],
  },
};

const DeliveryAddressModal = ({ open, onClose, onSaved }) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [zip, setZip] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [houseNumberAddition, setHouseNumberAddition] = useState("");
  const [addressLine, setAddressLine] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [allowUnknownAddition, setAllowUnknownAddition] = useState(false);
  const [countryCode, setCountryCode] = useState(COUNTRY_NL);
  const [currentStep, setCurrentStep] = useState(0);
  const [landelijk, setLandelijk] = useState(false);
  const [resolvedAddress, setResolvedAddress] = useState(null);

  useEffect(() => {
    if (open && typeof window !== "undefined") {
      setCurrentStep(0);
      const storedCountry =
        localStorage.getItem(STORAGE_DELIVERY_COUNTRY) || COUNTRY_NL;
      const storedZip = localStorage.getItem(STORAGE_DELIVERY_ZIP) || "";
      const storedHnr =
        localStorage.getItem(STORAGE_DELIVERY_HOUSENUMBER) || "";
      const storedAdd =
        localStorage.getItem(STORAGE_DELIVERY_HOUSENUMBER_ADDITION) || "";
      const storedStreet = localStorage.getItem(STORAGE_DELIVERY_STREET) || "";
      const storedCity = localStorage.getItem(STORAGE_DELIVERY_CITY) || "";

      setCountryCode(storedCountry);
      setZip(storedZip);
      setHouseNumber(storedHnr);
      setHouseNumberAddition(storedAdd);
      setAllowUnknownAddition(false);
      setAddressLine(null);
      setResolvedAddress(null);

      // Initialize form values
      form.setFieldsValue({
        country: storedCountry,
        zip: storedZip,
        house_number: storedHnr,
        house_number_addition: storedAdd || "",
      });

      // If we already have a stored full address, show it immediately
      if (
        storedCountry === COUNTRY_NL &&
        validateZipCode(storedZip, storedCountry) &&
        validateHouseNumber(storedHnr)
      ) {
        if (storedStreet || storedCity) {
          setResolvedAddress({
            street: storedStreet,
            city: storedCity,
            houseNumber: storedHnr,
            houseNumberAddition: storedAdd || undefined,
          });
          setAddressLine(
            `${storedStreet} ${storedHnr}${storedAdd ? ` ${storedAdd}` : ""}, ${storedCity}`,
          );
        } else {
          // No stored street/city -> try resolving immediately so user can just save
          getAddressLine(storedZip, storedHnr, storedAdd);
        }
      }
    }
  }, [open]);

  const saveAndNext = async () => {
    try {
      const values = await form.validateFields();
      const { country, zip, house_number, house_number_addition } = values;

      // For NL we require a resolved addressLine from lookup, for BE we allow without
      if (country === COUNTRY_NL && !addressLine) {
        messageApi.error(t("modal.invalid_address_combination_short"));
        return;
      }

      localStorage.setItem(STORAGE_DELIVERY_COUNTRY, country);
      localStorage.setItem(STORAGE_DELIVERY_ZIP, zip.trim());
      if (!!house_number?.trim())
        localStorage.setItem(STORAGE_DELIVERY_HOUSENUMBER, house_number.trim());
      // Persist house number addition (empty string if not provided)
      localStorage.setItem(
        STORAGE_DELIVERY_HOUSENUMBER_ADDITION,
        (house_number_addition || "").trim(),
      );
      // Persist street/city from postcode lookup (or clear if not available)
      localStorage.setItem(
        STORAGE_DELIVERY_STREET,
        (resolvedAddress?.street || "").trim(),
      );
      localStorage.setItem(
        STORAGE_DELIVERY_CITY,
        (resolvedAddress?.city || "").trim(),
      );
      if (onSaved)
        onSaved(
          country,
          zip.trim(),
          house_number.trim(),
          resolvedAddress?.street || "",
          resolvedAddress?.city || "",
          (house_number_addition || "").trim(),
        );
      setLandelijk(isLandelijk(zip.trim()));
      // Do not close here; go to step 2 (timeline)
      setCurrentStep(1);
    } catch (e) {
      // errors shown by form
    }
  };

  const getAddressLine = async (zip, houseNumber, addition) => {
    try {
      if (countryCode !== COUNTRY_NL) return; // Only NL lookup supported
      const address = await lookupAddress({
        zipCode: zip,
        houseNumber: houseNumber,
        addition: addition ?? "",
      });

      if (address) {
        setResolvedAddress(address);
        setAddressLine(
          `${address.street} ${address.houseNumber}${!!address.houseNumberAddition ? ` ${address.houseNumberAddition}` : ""}, ${address.city}`,
        );

        // If user entered an addition but lookup doesn't return one, prompt user
        if (
          !!addition &&
          !address.houseNumberAddition &&
          !allowUnknownAddition
        ) {
          notification.warning({
            message: t("errors.addition_not_found"),
            description: t("errors.addition_not_found_description", {
              number: houseNumber,
              addition: addition,
            }),
            key: "addition-modal",
            closeIcon: <div />,
            btn: (
              <div style={{ display: "flex", flexFlow: "wrap", gap: 6 }}>
                <Button
                  onClick={() => {
                    setHouseNumberAddition("");
                    // Reset address line without addition
                    setAddressLine(`${address.street} ${address.houseNumber}`);
                    setResolvedAddress({
                      ...address,
                      houseNumberAddition: undefined,
                    });
                    form.setFieldsValue({
                      house_number_addition: "",
                    });
                    notification.destroy("addition-modal");
                  }}
                  type="primary"
                >
                  {t("remove_addition")}
                </Button>
                <Button
                  onClick={() => {
                    setAllowUnknownAddition(true);
                    // Keep user's entered addition in display
                    setAddressLine(
                      `${address.street} ${address.houseNumber} ${addition}`,
                    );
                    setResolvedAddress({
                      ...address,
                      houseNumberAddition: addition,
                    });
                    notification.destroy("addition-modal");
                  }}
                  style={{ marginRight: 4 }}
                  type="primary"
                >
                  {t("keep_addition", { number: houseNumber, addition })}
                </Button>
              </div>
            ),
            duration: 0,
          });
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <StyledModal
      title={currentStep === 0 ? t("delivery_details_modal_title") : null}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={currentStep === 0 ? 600 : 750}
    >
      {contextHolder}
      {currentStep === 0 && (
        <>
          <p className={"description"}>
            {t("delivery_details_modal_description")}
          </p>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, all) => {
              // Keep state in sync for lookup logic
              if (all.country && all.country !== countryCode) {
                setCountryCode(all.country);
                localStorage.setItem(STORAGE_DELIVERY_COUNTRY, all.country);
                setAddressLine(null);
              }
              if (typeof all.zip !== "undefined") setZip(all.zip || "");
              if (typeof all.house_number !== "undefined")
                setHouseNumber(all.house_number || "");
              if (typeof all.house_number_addition !== "undefined")
                setHouseNumberAddition(all.house_number_addition || "");

              // Trigger lookup for NL when inputs look valid enough
              const c = all.country || countryCode;
              const z = typeof all.zip !== "undefined" ? all.zip : zip;
              const hn =
                typeof all.house_number !== "undefined"
                  ? all.house_number
                  : houseNumber;
              const add =
                typeof all.house_number_addition !== "undefined"
                  ? all.house_number_addition
                  : houseNumberAddition;
              if (
                c === COUNTRY_NL &&
                validateZipCode(z, c) &&
                validateHouseNumber(hn)
              ) {
                getAddressLine(z, hn, add);
              } else if (c === COUNTRY_BE) {
                setAddressLine(null);
              }
            }}
          >
            <Row gutter={12}>
              <Col xs={24}>
                <Form.Item
                  name="country"
                  label={t("country")}
                  rules={[
                    { required: true, message: t("errors.required_field") },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Select
                    options={[
                      { value: COUNTRY_NL, label: t("netherlands") },
                      { value: COUNTRY_BE, label: t("belgium") },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="zip"
                  label={t("zip_code")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_valid_zip_code"),
                    },
                    {
                      validator: (_, value) =>
                        validateZipCode(
                          value,
                          form.getFieldValue("country") || countryCode,
                        )
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(t("form.enter_valid_zip_code")),
                            ),
                    },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Input
                    placeholder={t("zip_code")}
                    maxLength={
                      (form.getFieldValue("country") || countryCode) ===
                      COUNTRY_BE
                        ? 4
                        : 7 /* allow a space for NL autocomplete like '1234 AB' */
                    }
                    onChange={(e) => {
                      const c = form.getFieldValue("country") || countryCode;
                      const raw = e.target.value;
                      const normalized =
                        c === COUNTRY_BE
                          ? raw.replace(/[^0-9]/g, "").trim()
                          : raw.replace(/\s+/g, "").toUpperCase().trim();
                      form.setFieldsValue({ zip: normalized });
                    }}
                    onBlur={() => {
                      const c = form.getFieldValue("country") || countryCode;
                      const raw = form.getFieldValue("zip") || "";
                      const normalized =
                        c === COUNTRY_BE
                          ? (raw || "").replace(/[^0-9]/g, "").trim()
                          : (raw || "")
                              .replace(/\s+/g, "")
                              .toUpperCase()
                              .trim();
                      form.setFieldsValue({ zip: normalized });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={14} md={8}>
                <Form.Item
                  name="house_number"
                  label={t("house_number")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_house_number"),
                    },
                    {
                      validator: (_, value) =>
                        validateHouseNumber(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(t("form.enter_valid_house_number")),
                            ),
                    },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Input
                    placeholder={t("house_number")}
                    onChange={(e) =>
                      form.setFieldsValue({
                        house_number: e.target.value.replace(/[^0-9]/g, ""),
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={10} md={8}>
                <Form.Item
                  name="house_number_addition"
                  label={t("house_number_addition")}
                  rules={[
                    {
                      validator: (_, value) =>
                        validateAddition(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                t("form.enter_valid_house_number_addition"),
                              ),
                            ),
                    },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Input
                    placeholder={t("house_number_addition")}
                    maxLength={2}
                    onChange={(e) =>
                      form.setFieldsValue({
                        house_number_addition: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          {addressLine && (
            <div style={{ marginBottom: 12 }}>
              <p>{addressLine}</p>
            </div>
          )}
          <Button
            type="primary"
            block
            onClick={saveAndNext}
            style={{ marginTop: addressLine ? 0 : 12 }}
          >
            {t("save")}
          </Button>
        </>
      )}
      {currentStep === 1 && (
        <div style={{ marginTop: 8 }}>
          {(() => {
            const lang = (i18n?.language || "nl").startsWith("nl")
              ? "nl"
              : "en";
            const data = landelijk
              ? nextStepsLandelijk[lang]
              : nextStepsRegional[lang];
            return (
              <>
                <h3 style={{ fontSize: 18, color: "#000" }}>{data.title}</h3>
                <Timeline
                  style={{ marginTop: 24 }}
                  items={data.steps.map((s, index) => ({
                    color: "green",
                    dot: s.dot,
                    children: (
                      <div>
                        <strong>{s.title}</strong>
                        <div style={{ color: "#666" }}>{s.description}</div>
                      </div>
                    ),
                  }))}
                />
              </>
            );
          })()}
          <Button
            style={{ marginTop: 12 }}
            type="primary"
            block
            onClick={onClose}
          >
            {t("close")}
          </Button>
        </div>
      )}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-header {
    margin-bottom: 0;
  }

  .description {
    margin-bottom: 16px;
  }
`;

export default DeliveryAddressModal;
